import { getAnalytics } from "@tm/insurance/analytics";
import {
  debounce,
  replaceHistory,
  replaceHistoryRemoveParams,
} from "@tm/insurance/utils";
import {
  AnchorTag,
  FlexComp,
  FormattedText,
  LandingButton,
  ParaTag,
  ReactPortalComp,
  SpanTag,
  TMFloatingInput,
} from "@tm/static";
import { usePlanType } from "@tm/ui";
import { theme } from "@tm/utility/global";
import Fuse from "fuse.js";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import WebToast from "../Toast/WebToast";
import CountryList from "./CountryList";

import { trackEvent } from "@tm/insurance/analytics/xdm";
import {
  BtnWrap,
  CountrySectionWrap,
  SelectCountryHeader,
  SelectCountrySheetStyle,
} from "./SelectCountryModalV2.style";

const analytics = getAnalytics();

const DEBOUNCE_TIME = 300;

const fuzzySearch = (
  searcher: Fuse<any>,
  searchValue: string,
  displayCountryData: any[],
  countryData: any[],
  setDisplayCountrysData: React.Dispatch<React.SetStateAction<any[]>>
) => {
  const selectedCountriesData = displayCountryData.filter(
    (country: any) => country.isSelected && !country.isDisabled
  );
  const searchResults = searcher.search(searchValue);
  const filteredData = searchResults?.map(
    (match: { item: any }) => match?.item
  );

  const originalSortData = countryData.map((country: any) => {
    return displayCountryData.find((item: any) => item.code === country.code);
  });

  const allObjects = [
    ...selectedCountriesData,
    ...filteredData,
    ...originalSortData,
  ];
  const tempObj = allObjects.reduce((acc: any, obj: any) => {
    if (obj && obj.code && !acc.hasOwnProperty(obj.code)) {
      acc[obj.code] = obj;
    }
    return acc;
  }, {});
  const uniqueObjects = Object.values(tempObj);
  setDisplayCountrysData(uniqueObjects);
};
const debouncedSearch = debounce(fuzzySearch, 300);

interface SelectCountryModalProps {
  handleSelectCountry: () => void;
  countryData: {
    code?: string;
    countryCount?: string;
    descTitle?: string;
    description?: string;
    isPopular?: boolean;
    keywords?: string;
    img?: string;
    subTitle?: string;
    isMandatory?: string;
    regionNames?: string;
    regionCodes?: string[];
    name?: string;
    type?: string;
  }[];
  countrySelectErrMsg?: string;
  popularRegion?: {
    id: number;
    regionName?: string;
    regionalCountryTxt?: string;
    countryList?: string[];
    isSelected?: boolean;
    regionInfo?: string;
  }[];
  showCountry: {
    code?: string;
    countryCount?: string;
    descTitle?: string;
    description?: string;
    isPopular?: boolean;
    keywords?: string;
    img?: string;
    subTitle?: string;
    isMandatory?: string;
    regionNames?: string;
    regionCodes?: string[];
    name?: string;
    type?: string;
  }[];
  customSubTitle?: string;
  setShowCountry: Dispatch<SetStateAction<any[] | undefined>>;
  setIsCountryError: Dispatch<SetStateAction<boolean>>;
  popularCountries?: {
    code: string;
    isMandatory: boolean;
    isPopular: boolean;
    name: string;
    keywords: string;
    flag: string;
    img: string;
  }[];
  handleDescriptionModal: (code: string) => string;
  setCountryData: Dispatch<SetStateAction<any[]>>;
  setPopularCountries: Dispatch<SetStateAction<any[]>>;
}
const SelectCountryModalSheet = ({
  handleSelectCountry,
  countryData,
  setShowCountry,
  setCountryData,
  showCountry,
  countrySelectErrMsg,
  setIsCountryError,
  popularCountries,
  setPopularCountries,
  handleDescriptionModal,
  customSubTitle,
}: SelectCountryModalProps) => {
  const [displayCountryData, setDisplayCountrysData] =
    useState<any[]>(countryData);
  const [searchTxt, setSearchTxt] = useState("");
  const [isSelectedError, setIsSelectedError] = useState<boolean>(false);
  const searchedCountriesOmn = useRef<string[]>([]);
  const selectedCountriesOmn = useRef<string[]>([]);
  const singleSearchSelection = useRef<boolean>(false);
  const [countrySelectToastMsg, setCountrySelectToastMsg] =
    useState<string>("");
  const { type } = usePlanType();

  const searcher = new Fuse(displayCountryData, {
    keys: [
      "name",
      "keywords",
      "regionName",
      "description",
      "code",
      "regionCodes",
    ],
    isCaseSensitive: false,
    shouldSort: true,
    findAllMatches: true,
    threshold: 0.5,
    minMatchCharLength: 1,
  });

  const noSearchResults = (addTimeOut: boolean) => {
    let allObjects = [];
    const selectedHeader = {
      type: "HEADER",
      name: "Selected Countries",
      code: "SELECTED_COUNTRIES",
    };
    const selectedCountriesData = displayCountryData.filter((country) => {
      return country.isSelected && !country.isDisabled;
    });
    const originalSortData = countryData.map((country) => {
      return displayCountryData.find((item) => item.code === country.code);
    });
    if (selectedCountriesData.length) {
      allObjects = [
        { ...selectedHeader },
        ...selectedCountriesData,
        ...originalSortData,
      ];
      const tempObj = allObjects.reduce((acc, obj) => {
        if (obj && obj.code && !acc.hasOwnProperty(obj.code)) {
          acc[obj.code] = obj;
        }
        return acc;
      }, {});
      const uniqueObjects = Object.values(tempObj);
      addTimeOut
        ? setTimeout(() => {
            setDisplayCountrysData(uniqueObjects);
          }, DEBOUNCE_TIME)
        : setDisplayCountrysData(uniqueObjects);
    } else {
      addTimeOut
        ? setTimeout(() => {
            setDisplayCountrysData(originalSortData);
          }, DEBOUNCE_TIME)
        : setDisplayCountrysData(originalSortData);
    }
  };
  useEffect(() => {
    noSearchResults(false);
    analytics?.trackLandingEvents(
      {
        actionType: `Ins_Country_popuploaded`,
      },
      "pageView",
      type
    );
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "popup-rendered",
        event_value: `Ins_Country_popuploaded`,
      },
    });
  }, []);

  const countryClickHandler = (code: string) => {
    handleDescriptionModal(
      displayCountryData.filter((data: any) => data.code === code)[0]
    );
    let updatedCountryArr = displayCountryData?.map((country: any) => {
      if (country.code === code) {
        if (country["isSelected"]) {
          country["isSelected"] = false;
          return { ...country };
        }
        /* istanbul ignore next */
        if (selectedCountriesOmn?.current?.length >= 30) {
          selectedCountriesOmn.current = [
            ...selectedCountriesOmn?.current?.slice(1),
            country.code,
          ];
        } else {
          selectedCountriesOmn.current = [
            ...selectedCountriesOmn?.current,
            country.code,
          ];
        }
        analytics?.trackLandingEvents(
          {
            actionType: `Ins_Country_popup_checkbox:${country.name}`,
          },
          "pageView",
          type
        );
        trackEvent({
          event_detail: {
            event_type: "action",
            event_name: "component_cta-clicked",
            event_value: `Ins_Country_popup_checkbox:${country.name}`,
          },
        });
        return { ...country, isSelected: true, isDisabled: false };
      }
      return { ...country, isDisabled: false };
    });

    const isExclusiveCountrySelected = updatedCountryArr.find(
      (item: any) => item.isExclusiveSelection && item.isSelected
    );

    updatedCountryArr = updatedCountryArr.map((country: any) => {
      if (isExclusiveCountrySelected) {
        return country.isExclusiveSelection
          ? { ...country }
          : { ...country, isSelected: false, isDisabled: true };
      } else if (updatedCountryArr.some((item: any) => item.isSelected)) {
        return country.isExclusiveSelection
          ? { ...country, isDisabled: true }
          : { ...country };
      }
      return { ...country };
    });

    const selectedCountryCodes = updatedCountryArr
      .filter((country: any) => country.isSelected && country.type === "REGION")
      .map((country: any) => {
        return country.code;
      });
    selectedCountryCodes.map((item: any) => {
      updatedCountryArr = updatedCountryArr.map((country: any) => {
        if (country.regionCodes && country.regionCodes.includes(item)) {
          return { ...country, isDisabled: true };
        }
        return { ...country };
      });
    });
    setDisplayCountrysData(updatedCountryArr);
    const popularCountryData = updatedCountryArr.filter(
      (country: any) => country.isSelected || country.isPopular
    );
    setPopularCountries([
      ...popularCountryData.filter(
        (country: any) => country.isSelected && !country.isDisabled
      ),
      ...popularCountryData.filter((country: any) => country.isDisabled),
      ...popularCountryData.filter(
        (country: any) => !country.isSelected && !country.isDisabled
      ),
    ]);
  };

  const handleSearchtxt = useCallback(
    (searchValue: string) => {
      debouncedSearch(
        searcher,
        searchValue,
        displayCountryData,
        countryData,
        setDisplayCountrysData
      );
    },
    [displayCountryData]
  );

  const onSearchChange = (searchValue: string) => {
    if (searchValue) {
      handleSearchtxt(searchValue);
    } else {
      noSearchResults(true);
    }
  };

  const searchQueryHighlighter = (ele: string) => {
    const withSpecialCharsSupport = searchTxt?.replace(
      /[.*+?^${}()|[\]\\]/g,
      "\\$&"
    );

    const searchRegex = new RegExp(`(${withSpecialCharsSupport})`, "ig");

    const highlighted = ele.replace(
      searchRegex,
      (match) => `<span class="highlight">${match}</span>`
    );

    return <ParaTag dangerousText={highlighted} />;
  };

  const isCheckedHandler = useCallback(
    (data: any) => {
      return data.isSelected;
    },
    [displayCountryData, searchTxt]
  );

  const handleCrossClick = () => {
    setPopularCountries(showCountry);
    if (searchedCountriesOmn?.current?.length) {
      analytics?.trackLandingEvents(
        {
          actionType: "searched_countries",
          payload: searchedCountriesOmn?.current?.join("|") + "|",
        },
        "pageView",
        type
      );
      searchedCountriesOmn.current = [];
    }
    let tempCountryData: any = displayCountryData.map((country: any) => {
      return { ...country, isSelected: false, isDisabled: false };
    });
    showCountry.map((data: any) => {
      tempCountryData = tempCountryData.map((country: any) => {
        if (data.code === country.code) {
          return {
            ...country,
            isSelected: data.isSelected,
            isDisabled: data.isDisabled,
          };
        }
        return { ...country };
      });
    });

    setDisplayCountrysData({ ...tempCountryData });
    handleSelectCountry();
  };

  const onCountrySubmit = () => {
    analytics?.trackLandingEvents(
      {
        actionType: "Ins_Country_popup_AddDestinationClicked",
      },
      "pageView",
      type
    );
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "Ins_Country_popup_AddDestinationClicked",
      },
    });
    setIsCountryError(false);
    if (searchedCountriesOmn?.current?.length) {
      analytics?.trackLandingEvents(
        {
          actionType: "searched_countries",
          payload: searchedCountriesOmn?.current?.join("|") + "|",
        },
        "pageView",
        type
      );
      searchedCountriesOmn.current = [];
    }
    if (selectedCountriesOmn?.current?.length) {
      analytics?.trackLandingEvents(
        {
          actionType: "selected_countries",
          payload: selectedCountriesOmn?.current?.join("|"),
        },
        "pageView",
        type
      );
      selectedCountriesOmn.current = [];
    }
    let countryDataArr = countryData.map((country: any) => {
      return { ...country, isSelected: false, isDisabled: false };
    });

    displayCountryData.map((data: any) => {
      countryDataArr = countryDataArr.map((country: any) => {
        if (country.code === data.code) {
          return {
            ...country,
            isSelected: data.isSelected,
            isDisabled: data.isDisabled,
          };
        }
        return { ...country };
      });
    });
    setCountryData(countryDataArr);
    setShowCountry(popularCountries);

    const selectedCountryCodes = displayCountryData
      .filter((item: any) => item.isSelected && !item.isDisabled)
      .map((item: any) => item.code);

    replaceHistoryRemoveParams("countries");
    replaceHistory("countries", selectedCountryCodes.join(","));
    const selectedNonPopCountries = displayCountryData.filter(
      (data: any) => data.isSelected && !data.isPopular
    );
    analytics?.trackLandingEvents(
      {
        actionType: "other_countries_clicked",
        payload: selectedNonPopCountries.map((c: any) => c.name).join("|"),
      },
      "pageView",
      type
    );
    // trackEvent({
    //   event_detail: {
    //     event_type: "action",
    //     event_name: "component_cta-clicked",
    //     event_value: "other_countries_clicked",
    //   },
    // });

    handleSelectCountry && handleSelectCountry();
  };

  const handleSearchTxt = (event: any) => {
    if (event.nativeEvent.inputType === "deleteContentBackward") {
      if (searchedCountriesOmn?.current?.length >= 30) {
        searchedCountriesOmn.current = [
          ...searchedCountriesOmn?.current?.slice(1),
          searchTxt,
        ];
      } else {
        searchedCountriesOmn.current = [
          ...searchedCountriesOmn?.current,
          searchTxt,
        ];
      }
    }
    setSearchTxt((_) => event.target.value);
    onSearchChange(event.target.value);
  };

  const searchClickAnalytics = () => {
    analytics?.trackLandingEvents(
      {
        actionType: "Ins_Country_popup_searchclicked",
      },
      "pageView",
      type
    );
    if (!singleSearchSelection.current) {
      singleSearchSelection.current = true;
      trackEvent({
        event_detail: {
          event_type: "action",
          event_name: "component_cta-clicked",
          event_value: "Ins_Country_popup_searchclicked",
        },
      });
    }
  };

  return (
    <ReactPortalComp>
      <SelectCountrySheetStyle>
        <SelectCountryHeader>
          <AnchorTag handleClick={handleCrossClick}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.4 19L5 17.6L10.6 12L5 6.4L6.4 5L12 10.6L17.6 5L19 6.4L13.4 12L19 17.6L17.6 19L12 13.4L6.4 19Z"
                fill="#808080"
              />
            </svg>
          </AnchorTag>

          <FlexComp flexDirection="column">
            <ParaTag fontSize="md" fontWeight="bold" color={theme.greyText}>
              <FormattedText value="Select Destinations" />
            </ParaTag>
            <SpanTag fontSize="xs" color={theme.greyText6}>
              <FormattedText value={customSubTitle} />
            </SpanTag>
          </FlexComp>
        </SelectCountryHeader>
        <div onClick={searchClickAnalytics}>
          <TMFloatingInput
            handleChange={(e) => handleSearchTxt(e)}
            inputValue={searchTxt}
            labelTxt="Search Country/Region"
          />
        </div>
        <CountrySectionWrap>
          {displayCountryData?.length ? (
            <CountryList
              countryData={
                searchTxt.length
                  ? displayCountryData.filter(
                      (country) => country.type !== "HEADER"
                    )
                  : displayCountryData
              }
              countryHandler={countryClickHandler}
              isCheckedHandler={isCheckedHandler}
              searchQueryHighlighter={searchQueryHighlighter}
              setCountryData={setDisplayCountrysData}
              setIsSelectedError={setIsSelectedError}
              showHeader={!searchTxt.length}
              setCountrySelectToastMsg={setCountrySelectToastMsg}
            />
          ) : (
            <ParaTag
              fontSize="sm"
              color={theme.greyText}
              margin="30px 0px 0px 0px"
            >
              No Country Found! Please enter correct name
            </ParaTag>
          )}
        </CountrySectionWrap>
        {isSelectedError && (
          <WebToast
            toastTxt={countrySelectErrMsg}
            position="fixed"
            height="40px"
            bottom="75px"
          />
        )}
        {displayCountryData?.length && (
          <BtnWrap position="fixed" bottom="0px" background="white">
            <LandingButton
              margin="12px 16px 12px 16px"
              customWidth="92%"
              customTitle={"Add Destination"}
              onClick={onCountrySubmit}
            />
          </BtnWrap>
        )}
      </SelectCountrySheetStyle>
    </ReactPortalComp>
  );
};

export default SelectCountryModalSheet;
